import React from 'react';
import { ComponentPage, DemoCard } from '../../shared';
import { SpacingExample, RadiusExample, TokenValue, useResponsiveTokens } from './tokenHelpers';

const SPACING = ['small-s', 'small-m', 'small-l', 'medium-s', 'medium-m', 'medium-l', 'large-s', 'large-m', 'large-l'];

/*
 * Type tokens come in font-size / line-height PAIRS, so a 3-up grid of loose
 * chips split every pair across rows and made the section unreadable. One row
 * per setting, with both values side by side, is what the data actually is.
 */
const TYPO_SETTINGS = [
  ['H1 Editorial', '--typo-h1-editorial-font-size', '--typo-h1-editorial-line-height'],
  ['H2 Editorial', '--typo-h2-editorial-font-size', '--typo-h2-editorial-line-height'],
  ['H1', '--typo-h1-font-size', '--typo-h1-line-height'],
  ['H2', '--typo-h2-font-size', '--typo-h2-line-height'],
  ['H3', '--typo-h3-font-size', '--typo-h3-line-height'],
  ['H4', '--typo-h4-font-size', '--typo-h4-line-height'],
  ['H5', '--typo-h5-font-size', '--typo-h5-line-height'],
  ['H6', '--typo-h6-font-size', '--typo-h6-line-height'],
  ['Paragraph Editorial Large', '--typo-p-editorial-large-font-size', '--typo-p-editorial-large-line-height'],
  ['Paragraph Editorial', '--typo-p-editorial-regular-font-size', '--typo-p-editorial-regular-line-height'],
  ['Paragraph Large', '--typo-p-large-font-size', '--typo-p-large-line-height'],
  ['Paragraph Regular', '--typo-p-regular-font-size', '--typo-p-regular-line-height'],
  ['Paragraph Small', '--typo-p-small-font-size', '--typo-p-small-line-height'],
  ['Input', '--typo-input-font-size', '--typo-input-line-height'],
];

const SPACING_TOKENS = [...SPACING.map((n) => `--space-ui-${n}`), '--space-layout-padding'];
const TYPO_TOKENS = TYPO_SETTINGS.flatMap(([, sizeVar, lineVar]) => [sizeVar, lineVar]);

/** `28px` — or an em dash while the measuring frames warm up. */
const value = (pair, band) => (pair?.[band] ? pair[band] : '—');

/** `40px / 64px` — a font-size and line-height pair at one breakpoint. */
const typePair = (size, line, band) => `${value(size, band)} / ${value(line, band)}`;

export const meta = {
  title: 'Spacing & Radius',
  group: 'Foundations',
  description:
    'Nine spacing steps and six radii, all responsive — they change at 1024px, and the radii also change per brand (CLS is sharp at 2–4px, ABC is soft at 6–12px). Every value on this page is read live from the cascade, so switch brand and the numbers follow. The two responsive tables resolve each token on both sides of the breakpoint, so they stay correct whatever width you are reading them at.',
  usage: [
    {
      title: 'In CSS',
      code: `.my-block {
  padding: var(--space-ui-medium-m) var(--space-ui-medium-l);
  gap: var(--space-ui-small-l);
  border-radius: var(--radius-card);
}

/* No media query needed — the token itself changes at 1024px */`,
    },
    {
      title: 'In JSX — DS utility classes',
      code: `// Utilities in src/index.css cover the same scale and are
// cheaper to read than arbitrary values
<p className="typo-h5 pb-small-l">Subtitle</p>
<div className="py-large-s px-medium-m">Block</div>
<ul className="gap-medium-s">…</ul>

// Prefixes: pt- pb- py- pl- pr- px- mt- mb- gap-
// Sizes:    small-s|m|l  medium-s|m|l  large-s|m|l

// Never Tailwind's own scale — p-4, gap-8 and friends
// are not design system values.`,
    },
  ],
};

export default function SpacingPage() {
  // One pair of measuring frames for every token on the page.
  const tokens = useResponsiveTokens([...SPACING_TOKENS, ...TYPO_TOKENS]);

  return (
    <ComponentPage meta={meta}>
      <DemoCard title="Spacing scale" hint="bar width is the live token value">
        <div className="space-y-[var(--space-ui-medium-s)]">
          {SPACING.map((name) => (
            <SpacingExample key={name} name={name} variable={`--space-ui-${name}`} />
          ))}
          <SpacingExample name="layout-padding" variable="--space-layout-padding" highlight />
        </div>
      </DemoCard>

      <DemoCard title="Border radius" hint="also changes per brand">
        <div className="flex flex-wrap gap-[var(--space-ui-medium-l)]">
          <RadiusExample name="Button" variable="--radius-button" />
          <RadiusExample name="Input" variable="--radius-input" />
          <RadiusExample name="Inner" variable="--radius-inner-radius" />
          <RadiusExample name="Card" variable="--radius-card" />
          <RadiusExample name="Popup" variable="--radius-popup" />
          <RadiusExample name="Drawer" variable="--radius-drawer" size="large" />
        </div>
      </DemoCard>

      <DemoCard title="Transitions">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[var(--space-ui-small-l)]">
          {['--transition-function-ui', '--transition-function-drawer', '--transition-time-button', '--transition-time-drawer', '--transition-time-hyperlink', '--transition-time-input'].map((name) => (
            <TokenValue key={name} name={name} />
          ))}
        </div>
      </DemoCard>

      <DemoCard title="Responsive spacing" hint="both breakpoints, read live">
        <div className="ds-page__table-scroll">
          <table className="ds-page__table">
            <thead>
              <tr>
                <th className="typo-label">Token</th>
                <th className="typo-label">Mobile · &lt;1024px</th>
                <th className="typo-label">Desktop · ≥1024px</th>
              </tr>
            </thead>
            <tbody className="typo-body-sm">
              {SPACING_TOKENS.map((name) => (
                <tr key={name}>
                  <td className="ds-page__table-name">{name}</td>
                  <td><code className="ds-page__table-default">{value(tokens[name], 'mobile')}</code></td>
                  <td><code className="ds-page__table-default">{value(tokens[name], 'desktop')}</code></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DemoCard>

      <DemoCard title="Responsive typography" hint="font-size / line-height at both breakpoints">
        <div className="ds-page__table-scroll">
          <table className="ds-page__table">
            <thead>
              <tr>
                <th className="typo-label">Setting</th>
                <th className="typo-label">Mobile · &lt;1024px</th>
                <th className="typo-label">Desktop · ≥1024px</th>
                <th className="typo-label">Token</th>
              </tr>
            </thead>
            <tbody className="typo-body-sm">
              {TYPO_SETTINGS.map(([label, sizeVar, lineVar]) => (
                <tr key={label}>
                  <td className="ds-page__table-name">{label}</td>
                  <td><code className="ds-page__table-default">{typePair(tokens[sizeVar], tokens[lineVar], 'mobile')}</code></td>
                  <td><code className="ds-page__table-default">{typePair(tokens[sizeVar], tokens[lineVar], 'desktop')}</code></td>
                  <td><code className="ds-page__table-type">{sizeVar.replace('--typo-', '').replace('-font-size', '')}</code></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DemoCard>
    </ComponentPage>
  );
}
